/**
 * AI Chat screen — live astrologer chat backed by POST /ai/complete.
 *
 * The server builds the astrologer persona + enforces every guardrail; the
 * client sends the user/assistant turns plus the user's birth data (via
 * `sendAiMessage`) and renders the real reply. No fabricated astrology: the
 * opening line is a generic greeting, every other message is either the user's
 * input or the live model's output (data-correctness — the previous static
 * "Меркурий в вашем 3-м доме…" placeholder invented personal positions).
 */
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, type ReactElement, useEffect, useRef, useState } from "react";

import { TabBar } from "@/components/TabBar";
import { type AiStreamResult, streamAiMessage, type WireChatMessage } from "@/lib/ai-api";
import { hapticImpact, hapticNotify } from "@/lib/tg";
import { useTgContext } from "@/state/tg";

interface ChatMsg {
  readonly id: string;
  readonly kind: "ai" | "user";
  readonly text: string;
}

/**
 * Copy for the two ways the server can end an answer early. Both are real
 * server decisions, so they get honest, specific text rather than the generic
 * failure line.
 */
const STOP_NOTICE: Readonly<Record<Exclude<AiStreamResult["stop"], "done">, string>> = {
  blocked:
    "Ответ не прошёл проверку безопасности и был остановлен. Переформулируйте вопрос, пожалуйста.",
  limit: "Дневной лимит запросов к астрологу исчерпан. Вернитесь завтра.",
};

const GREETING: ChatMsg = {
  id: "greeting",
  kind: "ai",
  text: "Здравствуйте! Спросите меня о вашей натальной карте, транзитах или совместимости — я отвечаю по реальному расчёту.",
};

let msgSeq = 0;
function nextId(prefix: string): string {
  msgSeq += 1;
  return `${prefix}-${msgSeq}`;
}

export default function AIChatPage(): ReactElement {
  const tg = useTgContext();
  const [messages, setMessages] = useState<readonly ChatMsg[]>([GREETING]);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  /**
   * The answer currently arriving, token by token. Kept out of `messages` so a
   * stream that gets blocked mid-flight can be dropped wholesale instead of
   * leaving a half-sentence in the transcript.
   */
  const [streaming, setStreaming] = useState<string | null>(null);

  const mutation = useMutation<AiStreamResult, Error, readonly WireChatMessage[]>({
    mutationFn: (history) => {
      setStreaming("");
      return streamAiMessage(tg.initData, history, (delta) => {
        setStreaming((prev) => (prev ?? "") + delta);
      });
    },
    onSuccess: (res) => {
      setStreaming(null);
      const text = res.stop === "done" ? res.text : STOP_NOTICE[res.stop];
      if (res.stop !== "done") hapticNotify("warning");
      setMessages((prev) => [...prev, { id: nextId("ai"), kind: "ai", text }]);
    },
    onError: () => {
      setStreaming(null);
      hapticNotify("error");
      setMessages((prev) => [
        ...prev,
        {
          id: nextId("err"),
          kind: "ai",
          text: "Не удалось получить ответ. Попробуйте ещё раз чуть позже.",
        },
      ]);
    },
  });

  // Keep the newest text in view as the answer streams in.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streaming]);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const text = draft.trim();
    if (!text || mutation.isPending) return;
    hapticImpact("light");
    const next: readonly ChatMsg[] = [...messages, { id: nextId("user"), kind: "user", text }];
    setMessages(next);
    setDraft("");
    // Wire history: real user/assistant turns only (skip the UI-only greeting),
    // ending with the new user turn (the server rejects a trailing non-user msg).
    const history: WireChatMessage[] = next
      .filter((m) => m.id !== "greeting")
      .map((m) => ({ role: m.kind === "ai" ? "assistant" : "user", content: m.text }));
    mutation.mutate(history);
  }

  return (
    <main className="screen">
      <div className="body-pad chat-scroll" ref={scrollRef}>
        <div className="chat-head">
          <div className="ai-av" aria-hidden="true">
            A
          </div>
          <div className="info">
            <div className="n">Астровелия</div>
            <div className="s">AI · Помнит вашу карту</div>
          </div>
        </div>

        {messages.map((m) => (
          <div key={m.id} className={`chat-msg ${m.kind}`}>
            {m.text}
          </div>
        ))}
        {/* The live answer. Shows a caret until the first token lands, so the
            user sees the astrologer thinking rather than a frozen screen. */}
        {streaming !== null ? (
          <div className="chat-msg ai" aria-live="polite" aria-busy={mutation.isPending}>
            {streaming.length > 0 ? streaming : "…"}
          </div>
        ) : null}
      </div>

      <form className="chat-input" onSubmit={handleSubmit}>
        <input
          aria-label="Сообщение"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          disabled={mutation.isPending}
          placeholder="Спросите о вашей карте…"
        />
        <button
          type="submit"
          className="chat-send"
          aria-label="Отправить"
          disabled={mutation.isPending}
        >
          ↑
        </button>
      </form>
      <TabBar active="ai" />
    </main>
  );
}
